import { throttle } from "../../utils/performance";
import { findRelatedElementByClassName } from "../../utils/DOMutils";
import animateValue, { updateHeight } from "../../utils/animateValue";
import {
    setAriaExapndedAttribute,
    setInertAttribute,
    setExpendableAttributes,
} from "../../utils/accessibility";
import { mediaMin } from "../../utils/breakpoints";
import Search from "./search";

export default function(el) {
    const navMenuInner = el.querySelector(".nav__menu-inner");
    const navLinks = el.querySelectorAll(".nav__menu .nav__link--has-children");
    const mobileNavToggle = el.querySelector(".nav__mobile-menu-toggle");
    const search = el.querySelector(".search");
    const searchToggle = el.querySelector(".nav__search-toggle");
    const logo = el.querySelector(".nav__logo");
    const brandMenu = el.querySelector(".nav__brand");
    const brandMenuClose = el.querySelector(".nav__brand");
    const breadcrumbs = el.querySelector(".nav__bc");
    let searchInput = null;

    const state = {
        menuOpened: false,
        searchOpened: false,
        brandMenuOpened: false,
        openedDropdownIndex: null,
        currentWidth: window.innerWidth,
        currentScrollY: window.scrollY,
    };

    const isDropdownOpened = () => state.openedDropdownIndex !== null && navLinks[state.openedDropdownIndex];

    const getDropdownElement = (navLink) => {
        const dropdown = navLink.nextElementSibling;

        if (!dropdown || !dropdown.classList.contains("nav__dropdown-wrapper")) return;

        return dropdown;
    };

    const updateDropdownHeight = (element, currentValue, progress) => {
        updateHeight(element, currentValue, progress);

        if (progress === 1 && isDropdownOpened() && navMenuInner && !mediaMin("lg", state.currentWidth))
            navMenuInner.scrollTo({
                top: navLinks[state.openedDropdownIndex].offsetTop,
                left: 0,
                behavior: "smooth",
            });
    };

    const animateDropdownHeight = (dropdown, actionType) =>
        animateValue(
            dropdown,
            updateDropdownHeight,
            dropdown.offsetHeight,
            actionType === "remove" ? 0 : dropdown.scrollHeight,
            mediaMin("nav") ? 150 : 250
        );

    const handleAnchorLinkClick = () => state.menuOpened && toggleMobileNavState();

    const toggleNavLink = (index, actionType = "add") => {
        const dropdown = getDropdownElement(navLinks[index]);

        if (!dropdown) return;

        if (mediaMin("nav", state.currentWidth) && actionType == "add")
            recalculateDropdownOverflow(navLinks[index], dropdown);

        navLinks[index].classList[actionType]("nav__link--active");

        animateDropdownHeight(dropdown, actionType);

        state.openedDropdownIndex = actionType === "remove" ? null : index;
        el.classList[isDropdownOpened() ? "add" : "remove"]("nav--dropdown-open");
        setAriaExapndedAttribute(navLinks[index], state.openedDropdownIndex !== null);
        setInertAttribute(dropdown, state.openedDropdownIndex !== null);
    };

    const handleNavLinkEvent = (index, e) => {
        e.preventDefault();

        const isClickEvent = !mediaMin("nav") && e.type === "click";
        const isMouseEnterEvent = mediaMin("nav") && e.type === "mouseenter";
        const isMouseLeaveEvent = mediaMin("nav") && e.type === "mouseleave";

        if (isClickEvent || isMouseEnterEvent || isMouseLeaveEvent) {
            const currentItem = isDropdownOpened() && state.openedDropdownIndex === index;

            if (isDropdownOpened()) toggleNavLink(state.openedDropdownIndex, "remove");

            if (state.searchOpened) toggleSearch();

            if (state.brandMenuOpened) toggleBrandMenu();

            if (!currentItem) toggleNavLink(index, isMouseLeaveEvent ? "remove" : undefined);
        }

        if (isClickEvent) setOutsideClickEventListener();
    };

    const toggleSearch = () => {
        state.searchOpened = !state.searchOpened;
        const actionType = state.searchOpened ? "add" : "remove";

        el.classList[actionType]("nav--search-open");
        setAriaExapndedAttribute(searchToggle, state.searchOpened);
        setInertAttribute(search, state.searchOpened);
    };

    const handleSearchToggleClick = () => {
        if (state.menuOpened) toggleMobileNavState();

        if (isDropdownOpened()) toggleNavLink(state.openedDropdownIndex, "remove");

        if (state.brandMenuOpened) toggleBrandMenu();

        toggleSearch();

        if (!searchInput) searchInput = el.querySelector(".search-input__input");

        if (searchInput) state.searchOpened ? searchInput.focus() : searchInput.blur();

        setOutsideClickEventListener();
    };

    const toggleBrandMenu = (actionType = null) => {
        state.brandMenuOpened = !state.brandMenuOpened;
        const aT = actionType ? actionType : state.brandMenuOpened ? "add" : "remove";

        animateDropdownHeight(brandMenu, aT);
        el.classList[aT]("nav--brand-open");
        setAriaExapndedAttribute(logo, state.brandMenuOpened);
        setInertAttribute(brandMenu, state.brandMenuOpened);
    };

    const handleLogoRightClick = (e) => {
        if (!mediaMin("nav")) return;

        e.preventDefault();

        if (state.menuOpened) toggleMobileNavState();

        if (isDropdownOpened()) toggleNavLink(state.openedDropdownIndex, "remove");

        if (state.searchOpened) toggleSearch();

        toggleBrandMenu();

        setOutsideClickEventListener();
    };

    const toggleMobileNavState = () => {
        state.menuOpened = !state.menuOpened;
        const actionType = state.menuOpened ? "add" : "remove";

        if (state.menuOpened && state.searchOpened) toggleSearch("remove");

        if (!state.menuOpened && isDropdownOpened()) toggleNavLink(state.openedDropdownIndex, actionType);

        if (state.brandMenuOpened) toggleBrandMenu();

        document.body.classList[actionType]("freeze");
        el.classList[actionType]("nav--mobile-menu-open");
        mobileNavToggle.classList[actionType]("hamburger--active");
    };

    const destroy = () => {
        if (state.menuOpened) toggleMobileNavState();

        if (isDropdownOpened()) toggleNavLink(state.openedDropdownIndex, "remove");

        if (state.searchOpened) toggleSearch();

        if (state.brandMenuOpened) toggleBrandMenu();

        setOutsideClickEventListener();
    };

    const recalculateDropdownOverflow = (navLink, dropdown) => {
        const navMainInner = el.querySelector(".nav__main-inner");

        if (!navMainInner) return;

        const navMainInnerRect = navMainInner && navMainInner.getBoundingClientRect();
        const navLinkRect = navLink.getBoundingClientRect();

        const calc = navLinkRect.x + dropdown.offsetWidth;
        const calc2 = navMainInnerRect.x + navMainInnerRect.width;

        if (calc > calc2) {
            const offset = Math.ceil(calc - calc2);
            dropdown.style.setProperty("transform", `translateX(-${offset}px)`);
        }
    };

    const handleOutsideClick = (e) => {
        const isInside = findRelatedElementByClassName(e.target, "parentElement", "nav");

        if (isInside) return;

        destroy();
    };

    const setOutsideClickEventListener = () =>
        state.menuOpened || isDropdownOpened() || state.searchOpened || state.brandMenuOpened
            ? document.addEventListener("click", handleOutsideClick, false)
            : document.removeEventListener("click", handleOutsideClick, false);

    const handleScrollEvent = throttle(() => {
        const actionType = window.scrollY > 0 ? "add" : "remove";
        el.classList[actionType]("nav--fixed");

        if (!breadcrumbs) return;

        const scrollY = window.scrollY;
        const breadcrumbsOffsetTop = breadcrumbs.offsetTop;
        const shiftedClass = "nav--shifted";

        if (scrollY < state.currentScrollY && el.classList.contains(shiftedClass)) {
            el.classList.remove("nav--shifted");
        } else if (
            state.currentScrollY > breadcrumbsOffsetTop &&
            scrollY > state.currentScrollY &&
            !el.classList.contains(shiftedClass)
        ) {
            el.classList.add("nav--shifted");
        }

        state.currentScrollY = scrollY;
    }, 10);

    const handleResizeEvent = throttle(() => {
        if (
            (mediaMin("nav") && !mediaMin("nav", state.currentWidth)) ||
            (!mediaMin("nav") && mediaMin("nav", state.currentWidth))
        )
            destroy();

        state.currentWidth = window.innerWidth;
    }, 150);

    el.querySelectorAll('a[href^="#"]').forEach((anchorLink) =>
        anchorLink.addEventListener("click", handleAnchorLinkClick)
    );

    if (mobileNavToggle) mobileNavToggle.addEventListener("click", toggleMobileNavState);

    navLinks.forEach((navLink, index) => {
        const dropdown = getDropdownElement(navLink);

        navLink.addEventListener("click", handleNavLinkEvent.bind(null, index));

        ["mouseenter", "mouseleave"].forEach((event) =>
            navLink.parentElement.addEventListener(event, handleNavLinkEvent.bind(null, index))
        );

        if (dropdown) setExpendableAttributes(navLink, dropdown);
    });

    if (searchToggle && search) {
        searchToggle.addEventListener("click", handleSearchToggleClick);
        Search(search);
        setExpendableAttributes(searchToggle, search);
    }

    if (brandMenu && logo) {
        logo.addEventListener("contextmenu", handleLogoRightClick);

        if (brandMenuClose) brandMenuClose.addEventListener("click", toggleBrandMenu.bind(null, "remove"));

        setExpendableAttributes(logo, brandMenu);
    }

    window.addEventListener("scroll", handleScrollEvent);
    window.addEventListener("resize", handleResizeEvent);

    handleScrollEvent();
}
